import { InvestmentProfile, Watchlist, Screener, ScreenerRule, ProfileOption, HeatmapCategories, HeatmapData, HeatmapsAllData } from '../types/investment'; 
import { DashboardWidget } from '../types/dashboard';
import { UserRole } from '../types/auth'; 
import { apiRequest, ApiRequestOptions, getBaseUrl, getFileContentFromFirebase } from '../services/apiService';
import { getFromCache, setInCache } from '../utils/memoryCache';
import { getStoredAuth } from '../utils/auth';

// Define the types for the status functions if using TypeScript
type AddStatusFunc = (message: string) => number;
type RemoveStatusFunc = (id: number) => void;

export interface InvestmentAPI {
  getProfiles: () => Promise<InvestmentProfile[]>;
  createProfile: (profile: { 
    name: string; 
    investment_profile_template: string; 
  }) => Promise<InvestmentProfile>;
  updateProfile: (profile: { 
    id: string;
    name: string;
    investment_profile_template: string;
    created_at: string;
  }) => Promise<InvestmentProfile>;
  test_get_file_from_firebase: () => Promise<any>;
  deleteProfile: (profileId: string) => Promise<void>;
  getWatchlists: (profileId: string) => Promise<Watchlist[]>;
  createWatchlist: (watchlist: Omit<Watchlist, 'id'>) => Promise<Watchlist>;
  updateWatchlist: (watchlist: Watchlist) => Promise<Watchlist>;
  deleteWatchlist: (profileId: string, watchlistId: string) => Promise<void>;
  getPreCannedScreeners: () => Promise<ScreenerRule[]>;
  getScreeners: (profileId: string) => Promise<Screener[]>;
  createScreener: (screener: Omit<Screener, 'id'>) => Promise<Screener>;
  deleteScreener: (profileId: string, screenerId: string) => Promise<void>;
  performScreening: (profileId: string, screenerName: string, queryRule: string) => Promise<string[]>;
  getTickerMap: () => Promise<Record<string, string> | null>;
  getAllTickersAllowed: () => Promise<any>;
  getTickerData: (ticker: string, profileId: string | null) => Promise<any>;
  getMultiTickerData: (ticker: Array<string>, profileId: string | null) => Promise<any>;
  getHistoricalData: (ticker: string) => Promise<any>;
  getMarketReport: (profile: string) => Promise<any>;
  getFreeReport: () => Promise<any>;
  getProfileOptions: () => Promise<ProfileOption[]>;
  updateScreener: (screener: Screener) => Promise<Screener>;
  getHeatmapCategories: () => Promise<HeatmapCategories>;
  getHeatmapData: (category: string, pivot: string) => Promise<HeatmapData>;
  getHeatmapsAll: () => Promise<HeatmapsAllData>; // Added new method
  changeRole: (role: UserRole, twitterHandle: string) => Promise<{ checkout_url?: string }> ;
  cancelSubscription: (twitterHandle: string) => Promise<void>;
  log: (messages: string[]) => Promise<void>;
}


/**
 * Creates an API client for investment-related operations
 * Accepts functions to add/remove loading status messages.
 */
export const investmentAPI = (
  addStatus: AddStatusFunc = () => 0,  
  removeStatus: RemoveStatusFunc = () => {}
): InvestmentAPI => {
  // Check if API is configured
  if (!getBaseUrl()) {
    console.warn('Warning: API base URL is not configured. Some operations may fail.');
  }
  console.log('Investment API initialized with base URL:', getBaseUrl());
  
  // Helper function to apply investor profile adjustments
  const applyInvestorProfileAdjustments = (tickerData: any, profileId: string): any => {
    // If no ticker data or no ip_adjustments, return original data
    if (!tickerData || !tickerData.ip_adjustments) {
      return tickerData;
    }
    let profileTemplate = profileId;
    console.log('Applying investor profile adjustments:', { profileId, tickerData });
    if (!profileTemplate || !tickerData.ip_adjustments[profileTemplate]) {
      return tickerData;
    }
    
    // Create a deep copy of the original data
    const result = JSON.parse(JSON.stringify(tickerData));
    
    // Store the original values in a "base" property
    result.base = {};
    
    // Get the profile-specific adjustments
    const adjustments = tickerData.ip_adjustments[profileTemplate];
    
    // Apply all the adjustments, storing original values in the base object
    Object.keys(adjustments).forEach(key => {
      if (key !== 'ticker' && key !== 'date') {  // Skip these fields
        if (key in result) {
          result.base[key] = result[key];
          result[key] = adjustments[key];
        } else {
          result[key] = adjustments[key]; // Add new fields directly
        }
      }
    });
    
    return result;
  };

  // Improved wrapper for apiRequest to handle status updates
  const apiRequestWithStatus = async <T>(endpoint: string, options: ApiRequestOptions, statusMessage: string): Promise<T> => {
    const statusId = addStatus(statusMessage);
    
    try {
      return await apiRequest<T>(endpoint, options);
    } catch (error) {
      // Rethrow but make sure we remove the status first
      console.error(`API request failed for ${endpoint}: ${error}`);
      throw error;
    } finally {
      // Always remove the status, even for errors
      removeStatus(statusId);
    }
  };

  // Return the implementation of the InvestmentAPI interface
  const interfacaceImplementation: InvestmentAPI = {
    test_get_file_from_firebase: async () => {
      // Now calls the common function from apiService
      console.log('Firebase Fetch (test): Calling getFileContentFromFirebase...');
      const statusId = addStatus(`Loading Firebase test file...`);
      try {
        const data = await getFileContentFromFirebase('/latest_ticker_report/MSFT.json'); // Use relative path from bucket root
        console.log('Firebase Fetch (test): Successfully got data:', data);
        return data;
      } catch (error) {
        console.error('Firebase Fetch (test): Error fetching Firebase content:', error);
        // throw error; // Re-throw error
      } finally {
        removeStatus(statusId);
      }
    },
    
    getProfiles: () => 
      apiRequestWithStatus<InvestmentProfile[]>('/investment_profiles', { method: 'GET' }, 'Loading profiles...'),

    getProfileOptions: () =>
      apiRequestWithStatus<ProfileOption[]>('/investment_profile_options', { method: 'GET' }, 'Loading profile options...'),
      
    createProfile: async (profile: { name: string; investment_profile_template: string; }): Promise<InvestmentProfile> => {
      console.log('investmentAPI.createProfile called with:', profile);
      return apiRequestWithStatus<InvestmentProfile>('investment_profiles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile)
      }, `Creating profile "${profile.name}"...`);
    },

    updateProfile: async (profile: { id: string; name: string; investment_profile_template: string; created_at: string; }): Promise<InvestmentProfile> => {
      console.log('Updating profile with:', profile);
      return apiRequestWithStatus<InvestmentProfile>('investment_profiles', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile)
      }, `Updating profile "${profile.name}"...`);
    },

    deleteProfile: async (profileId: string): Promise<void> => {
      return apiRequestWithStatus<void>(`investment_profiles?id=${profileId}`, {
        method: 'DELETE'
      }, `Deleting profile ${profileId}...`);
    },

    getWatchlists: async (profileId: string): Promise<Watchlist[]> => {
      return apiRequestWithStatus<Watchlist[]>(`watchlists?profile_id=${profileId}`, {
        method: 'GET'
      }, `Loading watchlists for profile ${profileId}...`);
    },

    createWatchlist: async (watchlist: Omit<Watchlist, 'id'>): Promise<Watchlist> => {
      return apiRequestWithStatus<Watchlist>('watchlists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(watchlist)
      }, `Creating watchlist "${watchlist.name}"...`);
    },

    updateWatchlist: async (watchlist: Watchlist): Promise<Watchlist> => {
      console.log('Updating watchlist:', watchlist);
      return apiRequestWithStatus<Watchlist>('watchlists', {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(watchlist)
      }, `Updating watchlist "${watchlist.name}"...`);
    },

    deleteWatchlist: async (profileId: string, watchlistId: string): Promise<void> => {
      return apiRequestWithStatus<void>(`watchlists?profile_id=${profileId}&id=${watchlistId}`, {
        method: 'DELETE'
      }, `Deleting watchlist ${watchlistId}...`);
    },

    getPreCannedScreeners: async (): Promise<ScreenerRule[]> => {
      const cacheKey = 'preCannedScreeners';
      return apiRequestWithStatus<ScreenerRule[]>('precanned_screeners', {
        method: 'GET',
        cacheKey
      }, 'Loading pre-canned screeners...');
    },

    getScreeners: async (profileId: string): Promise<Screener[]> => {
      console.log('Getting screeners for profile:', profileId);
      return apiRequestWithStatus<Screener[]>(`screeners?profile_id=${profileId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      }, `Loading screeners for profile ${profileId}...`);
    },

    createScreener: async (screener: Omit<Screener, 'id'>): Promise<Screener> => {
      console.log('Creating screener:', screener);
      return apiRequestWithStatus<Screener>('screeners', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(screener)
      }, `Creating screener "${screener.name}"...`);
    },

    deleteScreener: async (profileId: string, screenerId: string): Promise<void> => {
      return apiRequestWithStatus<void>(`screeners?profile_id=${profileId}&id=${screenerId}`, {
        method: 'DELETE'
      }, `Deleting screener ${screenerId}...`);
    },

    performScreening: async (profileId: string, screenerName: string, queryRule: string): Promise<string[]> => {
      console.log('Performing screening:', { profileId, screenerName, queryRule });
      const cacheKey = `screening-${queryRule}`;

      return apiRequestWithStatus<string[]>('perform_screening', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          profile_id: profileId,
          screener_name: screenerName,
          query_rule: queryRule
        }),
        cacheKey
      }, `Performing screening "${screenerName}"...`);
    },

    getTickerMap: async (): Promise<Record<string, string> | null> => {
      const cacheKey = 'tickerMap';
      let data = getFromCache<Record<string, string>>(cacheKey);
      if (data) {
        return data;
      }
      const statusId = addStatus(`Loading ticker map...`);
      try {
        try {
          data = await getFileContentFromFirebase('/latest_market_report/Ticker_Map.json'); // Use relative path from bucket root
          console.log('Firebase Fetch (getTickerMap): Successfully got data:', data);
        } catch (error) {
          console.error('Firebase Fetch (getTickerMap): Error fetching Firebase content. Getting from backend', error);
          data = await apiRequest<Record<string, string>>('get_ticker_map', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            cacheKey,
            skipCache: true // Skip the unified cache check since we check directly here
          });
        }
      } catch (error) {
        console.error('Error fetching ticker map:', error);
        throw error; // Re-throw after logging
      } finally {
        removeStatus(statusId); // Remove status after fetching
      }
      if (data) {
        setInCache(cacheKey, data); // Cache the fetched data
      }
      return data;
    },

    getAllTickersAllowed: async (): Promise<any> => {
      return apiRequestWithStatus<any>('all_tickers_allowed', {
        method: 'GET'
      }, 'Loading allowed tickers...');
    },

    getTickerData: async (ticker: string, profileId: string | null): Promise<any> => {
      const cacheKey = `tickerData-${ticker}`;
      const cData = getFromCache<any>(cacheKey);
      let data = null;
      console.log(`Fetching data for ticker (getTickerData): ${ticker} Profile - ${profileId}`);

      let statusId: number | null = null;
      try {
        if (cData) {
          console.log(`Cache hit for ${cacheKey}`, cData);
          data = [cData];
        } else {
          console.log(`Cache miss for ${cacheKey}`);
          statusId = addStatus(`Loading data for ${ticker}...`); // Add status only on cache miss
          try {
            data = await getFileContentFromFirebase(`/latest_ticker_report/${ticker}.json`); // Use relative path from bucket root
            console.log('Firebase Fetch (getTickerData): Successfully got data:', data);
            data = [data]; // Wrap in array to match expected format
          } catch (error) {
            console.error('Firebase Fetch (getTickerData): Error fetching Firebase content. Getting from backend', error);
            data = await apiRequest<any>(`ticker_data?tickers=${ticker}`, {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
              cacheKey,
              skipCache: true // Skip the unified cache check since we check individual ticker caches 
            });
          }
          if (data && data.length > 0) {
            setInCache(cacheKey, data[0]);
          }
        }
      } finally {
         if (statusId !== null) {
           removeStatus(statusId); // Remove status if it was added
         }
      }

      // Apply investor profile adjustments if a profile ID is provided
      if (data && data.length > 0 && profileId) {
        data = data.map((tickerData: any) => {
          return applyInvestorProfileAdjustments(tickerData, profileId);
        });
        console.log('Adjusted data:', data);  
      }
      
      return data;
    },

    getMultiTickerData: async (tickers: Array<string>, profileId: string | null): Promise<Record<string, any>> => {
      const aData: Record<string, any> = {};
  
      console.log('Fetching data for multiple tickers (getMultiTickerData):', tickers);
      
      // Create an array of promises for parallel execution with error handling
      const tickerPromises = tickers.map(async (ticker) => {
        try {
          const cData = getFromCache<any>(`tickerData-${ticker}`);
          if (cData) {
            return { ticker, data: cData, error: null };
          } else {
            const data = await interfacaceImplementation.getTickerData(ticker, profileId);
            const unwrappedData = data !== null ? data[0] : null;
            return { ticker, data: unwrappedData, error: null };
          }
        } catch (error) {
          console.error(`Error fetching data for ticker ${ticker}:`, error);
          return { ticker, data: null, error };
        }
      });
      
      // Wait for all promises to resolve in parallel
      const results = await Promise.allSettled(tickerPromises);
      
      // Build the result object
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { ticker, data } = result.value;
          aData[ticker] = data;
        } else {
          console.error('Promise rejected:', result.reason);
        }
      });
      
      console.log('Final ticker data: (getMultiTickerData)', profileId, aData);
      return aData;
    },

      // const aData: Record<string, any> = {};
      // // const remainingTickers: string[] = [];
      
      // console.log('Fetching data for multiple tickers (getMultiTickerData):', tickers);
      // Try to get cached data first
      // for (let ticker of tickers) {
      //   const cData = getFromCache<any>(`tickerData-${ticker}`);
      //   if (cData) {
      //     aData[ticker] = cData;
      //   } else {
      //     // remainingTickers.push(ticker);
      //     aData[ticker] = await interfacaceImplementation.getTickerData(ticker, profileId); // Call getTickerData to fetch data
      //     if (aData[ticker] !== null) {
      //       aData[ticker] = aData[ticker][0]; // Unwrap the array
      //     }
      //   }
      // }
      // console.log('Cached data (getMultiTickerData):', aData);
      // console.log('Remaining tickers to fetch (getMultiTickerData):', remainingTickers);
      // // If we have tickers that aren't cached, fetch them
      // if (remainingTickers.length > 0) {
      //   const remaining_tickers_str = remainingTickers.join(' ');
      //   console.log('Fetching data for remaining tickers:', remaining_tickers_str);
        
      //   let statusId: number | null = null;
      //   try {
      //     const batchCacheKey = `tickerBatch-${remainingTickers.sort().join('-')}`;
      //     statusId = addStatus(`Loading data for ${remainingTickers.length} tickers...`); // Add status for batch fetch
      //     const data = await apiRequest<any[]>(`ticker_data?tickers=${remaining_tickers_str}`, {
      //       method: 'GET',
      //       headers: { 'Content-Type': 'application/json' },
      //       skipCache: true, // Skip the unified cache check since we check individual ticker caches
      //       cacheKey: batchCacheKey // But still use a cache key for deduplication
      //     });
      //     console.log('Fetched data for tickers (getMultiTickerData):', data);
          
      //     // Store individual ticker data in cache and add to result
      //     data.forEach((d) => {
      //       if (d && d.ticker) {
      //         setInCache(`tickerData-${d.ticker}`, d);
      //         aData[d.ticker] = d;
      //       }
      //     });
      //   } catch (error) {
      //     console.error('Error in getMultiTickerData:', error);
      //     throw error; // Re-throw after logging
      //   } finally {
      //     if (statusId !== null) {
      //       removeStatus(statusId); // Remove batch status
      //     }
      //   }
      // }
      
      // // Apply investor profile adjustments if a profile ID is provided
      // if (profileId) {
      //   Object.keys(aData).forEach(ticker => {
      //     aData[ticker] = applyInvestorProfileAdjustments(aData[ticker], profileId);
      //   });
      // }
      
      // console.log('Final ticker data: (getMultiTickerData)', profileId, aData);
      // return aData;
    // },

    getHistoricalData: async (ticker: string): Promise<any> => {
      console.log(`***** Should not be called getHistoricalData: ${ticker}`);
      const cacheKey = `historicalData-${ticker}`;
      return apiRequestWithStatus<any>(`historical_ta?tickers=${ticker}`, {
        method: 'GET',
        cacheKey
      }, `Loading historical data for ${ticker}...`);
    },

    getMarketReport: async (template: string = "Balanced Growth Investor"): Promise<any> => {
      // Get the user's role from stored auth data
      const auth = getStoredAuth();
      const userRole = auth?.user?.role;
      
      // Determine which template to use based on user role
      let effectiveTemplate = template;
      if (userRole) {
        switch (userRole) {
          case 'unregistered':
            effectiveTemplate = "Unregistered_Report";
            break;
          case 'free':
            effectiveTemplate = "Free_Report";
            break;
          case 'base':
            effectiveTemplate = "Base_Report";
            break;
          default:
            // Use the provided template as fallback
            break;
        }
        console.log(`Using role-specific market report template: ${effectiveTemplate} for user role: ${userRole}`);
      }

      const cacheKey = `marketReport-${effectiveTemplate}`;
      let data = getFromCache<any>(cacheKey);
      if (data) {
        console.log(`Cache hit for ${cacheKey}`, data);
        return data;
      }
      const statusId = addStatus(`Loading market report (${effectiveTemplate})...`);
      try {
        data = await getFileContentFromFirebase(`/latest_market_report/${effectiveTemplate.replaceAll(' ', '_')}.json`); // Use relative path from bucket root
        console.log('Firebase Fetch (getMarketReport): Successfully got data:', data);
      } catch (error) {
        console.error('Error fetching market report:', error);
        // Fallback to API request if Firebase fetch fails
        console.log('Fetching market report from API as fallback...');
        data = await apiRequest<any>(`market_report?profile=${effectiveTemplate}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          cacheKey,
          skipCache: true // Skip the unified cache check since we check directly here
        });
      } finally {
        removeStatus(statusId); // Remove status after fetching
      }
      if (data) {
        setInCache(cacheKey, data); // Cache the fetched data
      }
      return data;
    },

    getFreeReport: async (): Promise<any> => {
      const cacheKey = 'freeData';
      return apiRequestWithStatus<any>('free_report', {
        method: 'GET',
        cacheKey
      }, 'Loading free report data...');
    },

    updateScreener: async (screener: Screener): Promise<Screener> => {
      console.log('Making updateScreener request:', {
        url: '/screeners',
        method: 'PUT',
        screener
      });

      return apiRequestWithStatus<Screener>('screeners', {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(screener)
      }, `Updating screener "${screener.name}"...`);
    },

    getHeatmapCategories: async (): Promise<HeatmapCategories> => {
      const cacheKey = 'heatmapCategories';
      return apiRequestWithStatus<HeatmapCategories>('heat_map_categories', {
        method: 'GET',
        cacheKey
      }, 'Loading heatmap categories...');
    },

    getHeatmapData: async (category: string, pivot: string): Promise<HeatmapData> => {
      const cacheKey = `heatmap-${category}-${pivot}`;
      return apiRequestWithStatus<HeatmapData>(`heat_map?category=${category}&pivot=${pivot}`, {
        method: 'GET',
        cacheKey
      }, `Loading heatmap data (${category}/${pivot})...`);
    },

    getHeatmapsAll: async (): Promise<HeatmapsAllData> => {
      const cacheKey = 'heatmapsAllData';
      return apiRequestWithStatus<HeatmapsAllData>('heat_maps_all', {
        method: 'GET',
        cacheKey,
        requireAuth: true // Assuming this requires auth based on API docs
      }, 'Loading all heatmap data...');
    },

    changeRole: async (role: UserRole, twitterHandle: string): Promise<{ checkout_url?: string }> => {
      return apiRequestWithStatus<{ checkout_url?: string }>('auth/change_role', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role, twitter_handle: twitterHandle }),
        requireAuth: true, // Ensure auth token is sent
        skipCache: true // Skip cache for this request
      }, `Changing role to ${role}...`);
    },

    cancelSubscription: async (twitterHandle: string): Promise<void> => {
      return apiRequestWithStatus<void>('auth/remove_member', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ twitter_handle: twitterHandle }),
        requireAuth: true, // Ensure auth token is sent
        skipCache: true // Skip cache for this request
      }, 'Cancelling subscription...');
    },

    log: async (messages: string[]): Promise<void> => {
      // const logMessage = messages.join(' ');
      console.log('Logging message:', messages);
      return apiRequest<void>('log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ log: messages }),
        requireAuth: false, // no token needed
        skipCache: true // Skip cache for this request
      });
    }
  };
  return interfacaceImplementation;
};
